/**
 * A terminal in a Sprite, as seen from the shell (§4.6.5).
 *
 * `/:pty` speaks binary frames for bytes and text frames for control, so this
 * is a thin thing: a socket, a fan-out, and base64 at the boundary. Bytes are
 * base64 because a capability message is JSON (§5.2) — the same message has to
 * survive a `MessagePort`, a WebSocket and, later, a platform bridge, and a
 * codec per transport is how protocols rot.
 *
 * Fan-out matters more than it looks. The app holds a `stream` capability and
 * the shell's terminal widget renders the same session; both subscribe here.
 * The app is never handed the socket, and the widget never learns the token.
 */

export type PtySpec = {
  /** The shell to run. Absent means the Sprite's own default. */
  command?: string;
  cols?: number;
  rows?: number;
};

export interface PtySession {
  /** base64 bytes, terminal-bound. */
  write(data: string): void;
  resize(cols: number, rows: number): void;
  /** Subscribe to output as base64 chunks. Returns the unsubscribe. */
  subscribe(handler: (data: string) => void): () => void;
  onClose(handler: (reason: string) => void): () => void;
  close(): void;
  readonly closed: boolean;
}

export type PtyOpener = (spec: PtySpec) => PtySession;

/** Where a Sprite is and what proves we may talk to it (§4.6.5). */
export type SpriteTarget = { origin: string; token: string };

const BRIDGE_PROTOCOL = "gelatos.v1";
const BEARER_PROTOCOL = "gelato.bearer.";

export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  // Chunked: `apply` on a 1MB array overflows the argument stack.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

export function fromBase64(data: string): Uint8Array {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** A session backed by a real WebSocket to a Sprite's `/:pty`. */
export function openSpritePty(target: SpriteTarget, spec: PtySpec): PtySession {
  const url = new URL("/:pty", target.origin);
  url.protocol = url.protocol === "http:" ? "ws:" : "wss:";
  if (spec.command !== undefined) url.searchParams.set("shell", spec.command);

  // The token rides in Sec-WebSocket-Protocol, never the query string: a URL is
  // logged by every proxy on the path and kept in history.
  const socket = new WebSocket(url.toString(), [
    BRIDGE_PROTOCOL,
    BEARER_PROTOCOL + target.token,
  ]);
  socket.binaryType = "arraybuffer";

  const subscribers = new Set<(data: string) => void>();
  const closers = new Set<(reason: string) => void>();
  const backlog: string[] = [];
  const pending: (string | Uint8Array)[] = [];
  let open = false;
  let closed = false;
  let size: { cols: number; rows: number } | null =
    spec.cols === undefined || spec.rows === undefined
      ? null
      : { cols: spec.cols, rows: spec.rows };

  const flush = (): void => {
    for (const item of pending.splice(0)) socket.send(item);
  };

  socket.onopen = () => {
    open = true;
    if (size !== null) socket.send(JSON.stringify(size));
    flush();
  };
  socket.onmessage = (event: MessageEvent) => {
    const data =
      event.data instanceof ArrayBuffer
        ? toBase64(new Uint8Array(event.data))
        : toBase64(new TextEncoder().encode(String(event.data)));
    if (subscribers.size === 0) {
      // Output that arrives before anyone is listening is the shell's banner,
      // and dropping it would make every terminal start blank.
      backlog.push(data);
      return;
    }
    for (const handler of subscribers) handler(data);
  };
  const finish = (reason: string): void => {
    if (closed) return;
    closed = true;
    for (const handler of closers) handler(reason);
    subscribers.clear();
    closers.clear();
  };
  socket.onerror = () => finish("the terminal connection failed");
  socket.onclose = (event: CloseEvent) =>
    finish(event.reason === "" ? "the terminal closed" : event.reason);

  const send = (item: string | Uint8Array): void => {
    if (closed) return;
    if (open) socket.send(item);
    else pending.push(item);
  };

  return {
    write(data: string): void {
      send(fromBase64(data));
    },
    resize(cols: number, rows: number): void {
      size = { cols, rows };
      send(JSON.stringify({ cols, rows }));
    },
    subscribe(handler: (data: string) => void): () => void {
      subscribers.add(handler);
      for (const item of backlog.splice(0)) handler(item);
      return () => subscribers.delete(handler);
    },
    onClose(handler: (reason: string) => void): () => void {
      if (closed) {
        handler("the terminal closed");
        return () => undefined;
      }
      closers.add(handler);
      return () => closers.delete(handler);
    },
    close(): void {
      finish("closed");
      socket.close();
    },
    get closed(): boolean {
      return closed;
    },
  };
}
